/**
 * Toolbar Menu Controller
 * Manages the 6-button application menu bar: File|Edit|Design|View|Window|Help
 *
 * Implements the WAI-ARIA Menubar pattern (APG):
 *   - role="menubar" container with role="menuitem" triggers
 *   - role="menu" popups with role="menuitem" / "menuitemcheckbox" / "menuitemradio"
 *   - Roving tabindex across menubar items (Arrow Left/Right)
 *   - Arrow Up/Down within open menus, Home/End, Enter/Space activation
 *   - Escape closes menu, restores focus to menubar trigger
 *
 * Menu panels are positioned absolutely and dismissed on click-outside or Escape.
 * No modal-manager or focus-trap — keyboard navigation is fully managed here.
 *
 * @license GPL-3.0-or-later
 */

import { keyboardConfig, formatShortcut } from './keyboard-config.js';
import { announce, withdrawAnnouncement } from './announcer.js';
import { getUIModeController } from './ui-mode-controller.js';
import {
  showWorkflowProgress,
  hideWorkflowProgress,
} from './workflow-progress.js';

/** Ordered menu identifiers matching the toolbar button order */
const MENU_IDS = ['file', 'edit', 'design', 'view', 'window', 'help'];

/** Human-readable labels for each menu */
const MENU_LABELS = {
  file: 'File',
  edit: 'Edit',
  design: 'Design',
  view: 'View',
  window: 'Window',
  help: 'Help',
};

/** Human-readable labels for radio groups */
const RADIO_GROUP_LABELS = {
  displayMode: 'Display Mode',
  projection: 'Projection',
  interfaceMode: 'Interface Mode',
  previewQuality: 'Preview Quality',
  edgeDetail: 'Edge Detail Limit',
};

/**
 * Access keys, transcribed from upstream's menu tree where `&`
 * marks the underlined letter. Each entry is this app's own label with the
 * ampersand placed on the letter upstream marks; labels this app adapted keep
 * the same letter where it survives, and items upstream does not have get no
 * access key rather than an invented one.
 *
 * Display only: there is no Alt+letter activation. Alt+F and Alt+D
 * open the browser's own menus, so coverage would differ per browser and a
 * user could not rely on any of it. Arrow and first-letter navigation inside
 * a menu are unchanged.
 */
const MNEMONIC_SOURCES = [
  // File
  '&New File',
  '&Open File…',
  'Recen&t Files',
  '&Examples',
  '&Reload',
  '&Close Project',
  '&Save',
  'Save &As…',
  'E&xport',
  'Show &Library Folder…',
  // Export submenu
  'Export as &STL (ascii)…',
  'Export as &STL (binary)…',
  'Export as &OBJ…',
  'Export as &OFF…',
  'Export as &WRL…',
  'Export as &AMF…',
  'Export as &3MF…',
  'Export as &DXF…',
  'Export as S&VG…',
  'Export as &Image…',
  // Edit
  '&Undo',
  '&Redo',
  'Cu&t',
  '&Copy',
  '&Paste',
  '&Indent',
  'U&nindent',
  'C&omment',
  'Unco&mment',
  'Conv&ert Tabs to Spaces',
  'Copy viewport ima&ge',
  'Copy viewport transl&ation',
  'Cop&y viewport rotation',
  'Copy vie&wport distance',
  'Copy vie&wport field of view',
  '&Find…',
  'Fin&d and Replace…',
  'Find Ne&xt',
  'Find Pre&vious',
  'Use Se&lection for Find',
  'Increase Font &Size',
  'Decrease Font Si&ze',
  '&Preferences (Keyboard Shortcuts)…',
  // Design
  '&Automatic Reload and Preview',
  '&Reload and Preview',
  '&Preview',
  'R&ender',
  '&3D Print',
  'Measure &Distance',
  'Measure &Angle',
  '&Check Validity',
  'Display A&ST…',
  'Display CSG &Tree…',
  'Display CSG Pr&oducts…',
  '&Flush Caches',
  // View
  '&Top',
  '&Bottom',
  '&Left',
  '&Right',
  '&Front',
  'Bac&k',
  '&Diagonal',
  'Ce&nter',
  '&Perspective',
  '&Orthogonal',
  // Help
  '&About',
  '&OpenSCAD Homepage',
  '&Documentation',
  '&Offline Documentation',
  '&Cheat Sheet',
  '&Offline Cheat Sheet',
  '&Library info',
];

/** label → index of the character to underline. */
const MNEMONICS = new Map(
  MNEMONIC_SOURCES.map((source) => {
    const index = source.indexOf('&');
    return [source.slice(0, index) + source.slice(index + 1), index];
  })
);

export class ToolbarMenuController {
  constructor() {
    /** @type {Map<string, HTMLElement>} menuId -> toolbar button element */
    this._buttons = new Map();

    /** @type {Map<string, HTMLElement>} menuId -> modal/popup wrapper element */
    this._modals = new Map();

    /** @type {string|null} ID of the currently open menu, or null */
    this._openMenuId = null;

    /** @type {Function|null} Optional global action callback */
    this._onMenuAction = null;

    /**
     * Per-menu builder functions called on each open to get fresh item definitions.
     * @type {Map<string, Function>}
     */
    this._menuBuilders = new Map();

    /** @type {number} Auto-incrementing counter for unique element IDs */
    this._idCounter = 0;

    /** @type {number} Index of the currently-focused menubar item (roving tabindex) */
    this._activeBarIndex = 0;

    /** @type {Function|null} Bound reference for document click-outside handler */
    this._onDocumentClick = null;

    /**
     * The last element focused outside the menu system. Opening a menu takes
     * focus, which collapses the editor's selection — so Cut/Copy/Paste and
     * the context-aware Undo need to know where the user actually was.
     * @type {HTMLElement|null}
     */
    this._lastExternalFocus = null;
  }

  /**
   * Initialize: discover toolbar buttons and menu panels, wire up keyboard
   * and click handlers for the full APG menubar pattern.
   *
   * @param {Object} [options]
   * @param {Function} [options.onMenuAction] - Called with (actionId, data) for each menu action
   */
  init(options = {}) {
    this._onMenuAction = options.onMenuAction || null;

    const bar = document.getElementById('toolbarMenuBar');
    if (!bar) {
      console.warn('[ToolbarMenuController] #toolbarMenuBar not found in DOM');
      return;
    }

    for (const menuId of MENU_IDS) {
      const btn = document.getElementById(`${menuId}MenuBtn`);
      const modal = document.getElementById(`${menuId}MenuModal`);

      if (!btn || !modal) {
        console.warn(
          `[ToolbarMenuController] Missing button or modal for menu: ${menuId}`
        );
        continue;
      }

      this._buttons.set(menuId, btn);
      this._modals.set(menuId, modal);

      btn.addEventListener('click', () => this._onBarItemClick(menuId));

      // Wired once here, not in renderMenuContent: the list element survives
      // every rebuild, so a listener added per opening would run once per
      // opening for every key.
      const listEl = modal.querySelector(`#${menuId}MenuItems`);
      if (listEl) {
        listEl.addEventListener('keydown', (e) =>
          this._handleMenuKeydown(e, menuId)
        );
      }

      // Overlay click closes the menu
      const overlay = modal.querySelector('.toolbar-menu-overlay');
      if (overlay) {
        overlay.addEventListener('click', () => this.closeAllMenus());
      }

      // Close button (mouse/touch affordance, not keyboard-reachable)
      const closeBtn = modal.querySelector('.toolbar-menu-close');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.closeAllMenus());
      }
    }

    // Menubar keyboard handler (arrow navigation between top-level items)
    bar.addEventListener('keydown', (e) => this._handleMenubarKeydown(e));

    document.addEventListener('focusin', (e) => {
      if (!this._isInsideMenus(e.target)) this._lastExternalFocus = e.target;
      // A bar button focused any way at all is the one the bar's keys act on.
      const barIndex = this._getVisibleBarItems().indexOf(e.target);
      if (barIndex >= 0) this._activeBarIndex = barIndex;
    });
  }

  /** @private */
  _isInsideMenus(el) {
    if (!el || typeof el.closest !== 'function') return false;
    if (el.closest('#toolbarMenuBar')) return true;
    for (const modal of this._modals.values()) {
      if (modal.contains(el)) return true;
    }
    return false;
  }

  /**
   * The element that held focus before the menu bar took it. Null once that
   * element leaves the document.
   * @returns {HTMLElement|null}
   */
  getLastExternalFocus() {
    const el = this._lastExternalFocus;
    return el && el.isConnected ? el : null;
  }

  /**
   * Put focus back where the user was. CodeMirror restores its own selection
   * on focus, which is what makes Cut and Copy work from a menu at all.
   */
  restoreExternalFocus() {
    this.getLastExternalFocus()?.focus?.();
  }

  // ============================================================================
  // Public API (consumed by main.js, hfm-controller.js)
  // ============================================================================

  /**
   * Open a menu popup.
   * @param {string} menuId
   */
  openMenu(menuId) {
    if (!this._modals.has(menuId)) return;

    // Close any currently open sibling menu first
    if (this._openMenuId !== null && this._openMenuId !== menuId) {
      this._hideMenu(this._openMenuId, { restoreFocus: false });
    }

    this._showMenu(menuId);
  }

  /**
   * Close a specific menu popup.
   * @param {string} menuId
   */
  closeMenu(menuId) {
    if (this._openMenuId === menuId) {
      this._hideMenu(menuId);
    }
  }

  /** Close all open menu popups (at most one can be open at a time). */
  closeAllMenus() {
    if (this._openMenuId !== null) {
      this._hideMenu(this._openMenuId);
    }
  }

  /** Alias used by some call sites. */
  closeAll() {
    this.closeAllMenus();
  }

  /** Show the entire toolbar menu bar. */
  show() {
    const bar = document.getElementById('toolbarMenuBar');
    if (bar) bar.classList.remove('hidden');
  }

  /** Hide the entire toolbar menu bar. */
  hide() {
    const bar = document.getElementById('toolbarMenuBar');
    if (bar) bar.classList.add('hidden');
  }

  /**
   * Show only the specified menu buttons; hide all others.
   * @param {string[]} visibleMenuIds
   */
  setVisibleMenus(visibleMenuIds) {
    const bar = document.getElementById('toolbarMenuBar');
    if (!bar) return;

    if (visibleMenuIds.length > 0) {
      bar.classList.remove('hidden');
    } else {
      bar.classList.add('hidden');
      return;
    }

    for (const menuId of MENU_IDS) {
      const btn = this._buttons.get(menuId);
      if (!btn) continue;
      if (visibleMenuIds.includes(menuId)) {
        btn.classList.remove('hidden');
      } else {
        btn.classList.add('hidden');
      }
    }
  }

  /**
   * Register a builder function for a menu.
   * @param {string} menuId
   * @param {Function} builderFn - () => Object[]
   */
  registerMenuBuilder(menuId, builderFn) {
    this._menuBuilders.set(menuId, builderFn);
  }

  /**
   * Render menu items into a menu's list container.
   * @param {string} menuId
   * @param {Object[]} items
   */
  renderMenuContent(menuId, items) {
    const modal = this._modals.get(menuId);
    if (!modal) return;

    const listEl = modal.querySelector(`#${menuId}MenuItems`);
    if (!listEl) return;

    listEl.innerHTML = '';

    let i = 0;
    while (i < items.length) {
      const item = items[i];
      if (item.type === 'radio') {
        const group = item.group;
        const radioItems = [];
        while (
          i < items.length &&
          items[i].type === 'radio' &&
          items[i].group === group
        ) {
          radioItems.push(items[i]);
          i++;
        }
        this._buildRadioGroup(group, radioItems).forEach((el) =>
          listEl.appendChild(el)
        );
      } else {
        listEl.appendChild(this._buildMenuItem(item));
        i++;
      }
    }
  }

  /**
   * Rebuild a menu's content from its registered builder.
   * @param {string} menuId
   */
  refreshMenuState(menuId) {
    const builder = this._menuBuilders.get(menuId);
    if (builder) {
      this.renderMenuContent(menuId, builder());
    }
  }

  // ============================================================================
  // Menu open/close helpers (no modal-manager)
  // ============================================================================

  /** @private */
  _showMenu(menuId) {
    this.refreshMenuState(menuId);

    const modal = this._modals.get(menuId);
    const btn = this._buttons.get(menuId);

    this._openMenuId = menuId;
    btn.setAttribute('aria-expanded', 'true');

    // Publish the trigger's position as custom properties so Classic can
    // drop the panel under its own menu-bar button, the way a desktop menu
    // behaves. These are anchor values, not layout styles — classic.css
    // decides whether to use them and the other modes ignore them.
    const rect = btn.getBoundingClientRect();
    modal.style.setProperty('--menu-anchor-x', `${Math.round(rect.left)}px`);
    modal.style.setProperty('--menu-anchor-y', `${Math.round(rect.bottom)}px`);

    modal.classList.remove('hidden');
    modal.setAttribute('aria-hidden', 'false');

    // Anchored panels for right-edge triggers (Help on a phone) would run
    // off screen; desktop menus shift left to stay visible. Measure the
    // rendered panel and re-anchor — CSS alone cannot shift by own width.
    const content = modal.querySelector('.toolbar-menu-content');
    if (content) {
      const panel = content.getBoundingClientRect();
      const margin = 8;
      if (panel.width > 0 && panel.right > window.innerWidth - margin) {
        const shifted = Math.max(
          margin,
          window.innerWidth - margin - panel.width
        );
        modal.style.setProperty('--menu-anchor-x', `${Math.round(shifted)}px`);
      }
    }

    // Focus the first menuitem inside the menu list
    const listEl = modal.querySelector(`#${menuId}MenuItems`);
    if (listEl) {
      const firstItem = this._getNavigableItems(listEl)[0];
      if (firstItem) {
        this._focusMenuItem(listEl, firstItem);
      }
    }

    announce(`${MENU_LABELS[menuId]} menu opened`, {
      clearDelayMs: 1500,
      source: 'menu',
    });
  }

  /**
   * @private
   * @param {string} menuId
   * @param {{restoreFocus?: boolean}} [options] - false when another menu is
   *   about to take focus
   */
  _hideMenu(menuId, { restoreFocus = true } = {}) {
    const modal = this._modals.get(menuId);
    const btn = this._buttons.get(menuId);
    // A closed menu is no longer open: its sentence is not said late.
    withdrawAnnouncement('menu');

    // Read before hiding: a focused item inside a hidden menu is focus lost.
    // Focus somewhere else on the page belongs to whoever put it there.
    const active = document.activeElement;
    const focusWasInMenu =
      modal.contains(active) || !active || active === document.body;

    modal.classList.add('hidden');
    modal.setAttribute('aria-hidden', 'true');
    btn.setAttribute('aria-expanded', 'false');

    if (this._openMenuId === menuId) {
      this._openMenuId = null;
    }

    if (restoreFocus && focusWasInMenu) {
      btn.focus();
    }
  }

  // ============================================================================
  // Menubar keyboard handling (roving tabindex across top-level items)
  // ============================================================================

  /** @private */
  _onBarItemClick(menuId) {
    if (this._openMenuId === menuId) {
      this.closeAllMenus();
    } else {
      this._activeBarIndex = this._getVisibleBarItems().indexOf(
        this._buttons.get(menuId)
      );
      this.openMenu(menuId);
    }
  }

  /**
   * Get the ordered list of visible menubar buttons.
   * @returns {HTMLElement[]}
   * @private
   */
  _getVisibleBarItems() {
    return MENU_IDS.map((id) => this._buttons.get(id)).filter(
      (btn) => btn && !btn.classList.contains('hidden')
    );
  }

  /**
   * Move roving tabindex to a specific menubar button.
   * @param {HTMLElement[]} items
   * @param {number} index
   * @private
   */
  _focusBarItem(items, index) {
    for (let i = 0; i < items.length; i++) {
      items[i].setAttribute('tabindex', i === index ? '0' : '-1');
    }
    items[index].focus();
    this._activeBarIndex = index;
  }

  /**
   * Keyboard handler on the menubar container.
   * @param {KeyboardEvent} e
   * @private
   */
  _handleMenubarKeydown(e) {
    const items = this._getVisibleBarItems();
    if (items.length === 0) return;

    // The focused button decides; the last one used only when focus is not
    // on a bar button.
    const focused = items.indexOf(document.activeElement);
    if (focused >= 0) this._activeBarIndex = focused;
    const idx = this._activeBarIndex;

    switch (e.key) {
      case 'ArrowRight': {
        e.preventDefault();
        const next = (idx + 1) % items.length;
        this._focusBarItem(items, next);
        if (this._openMenuId !== null) {
          const nextId = this._menuIdForButton(items[next]);
          if (nextId) this.openMenu(nextId);
        }
        break;
      }
      case 'ArrowLeft': {
        e.preventDefault();
        const prev = (idx - 1 + items.length) % items.length;
        this._focusBarItem(items, prev);
        if (this._openMenuId !== null) {
          const prevId = this._menuIdForButton(items[prev]);
          if (prevId) this.openMenu(prevId);
        }
        break;
      }
      case 'ArrowDown':
      case 'Enter':
      case ' ': {
        e.preventDefault();
        const targetId = this._menuIdForButton(items[idx]);
        if (targetId) {
          if (this._openMenuId === targetId) {
            // Already open — focus first menu item
            const modal = this._modals.get(targetId);
            const listEl = modal?.querySelector(`#${targetId}MenuItems`);
            if (listEl) {
              const first = this._getNavigableItems(listEl)[0];
              if (first) this._focusMenuItem(listEl, first);
            }
          } else {
            this.openMenu(targetId);
          }
        }
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const targetIdUp = this._menuIdForButton(items[idx]);
        if (targetIdUp) {
          if (this._openMenuId !== targetIdUp) {
            this.openMenu(targetIdUp);
          }
          // Focus last item in the menu
          const modal = this._modals.get(targetIdUp);
          const listEl = modal?.querySelector(`#${targetIdUp}MenuItems`);
          if (listEl) {
            const navItems = this._getNavigableItems(listEl);
            if (navItems.length > 0) {
              this._focusMenuItem(listEl, navItems[navItems.length - 1]);
            }
          }
        }
        break;
      }
      case 'Escape':
        if (this._openMenuId !== null) {
          e.preventDefault();
          this.closeAllMenus();
        }
        break;
      case 'Home': {
        e.preventDefault();
        this._focusBarItem(items, 0);
        break;
      }
      case 'End': {
        e.preventDefault();
        this._focusBarItem(items, items.length - 1);
        break;
      }
      default:
        break;
    }
  }

  /**
   * Look up the menuId for a given toolbar button element.
   * @param {HTMLElement} btn
   * @returns {string|null}
   * @private
   */
  _menuIdForButton(btn) {
    for (const [id, el] of this._buttons) {
      if (el === btn) return id;
    }
    return null;
  }

  // ============================================================================
  // Menu-level keyboard handling (Arrow Up/Down within an open menu)
  // ============================================================================

  /**
   * Get all navigable (non-disabled) menu items within a menu list.
   * Includes items inside nested role="group" containers but not items
   * inside nested role="menu" submenus (those get their own handler).
   * @param {HTMLElement} listEl - The <ul role="menu"> element
   * @returns {HTMLElement[]}
   * @private
   */
  _getNavigableItems(listEl) {
    const all = listEl.querySelectorAll(
      '[role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"]'
    );
    // Items inside nested submenus are left out. A grayed-out item is kept,
    // as the menu pattern asks: the arrow keys reach it, so a keyboard or
    // screen reader user learns it exists and why it is off; Enter on it
    // does nothing, since it has no click handler.
    return Array.from(all).filter(
      (el) => el.closest('[role="menu"]') === listEl
    );
  }

  /**
   * Set roving tabindex within a menu and focus the target item.
   * @param {HTMLElement} listEl
   * @param {HTMLElement} target
   * @private
   */
  _focusMenuItem(listEl, target) {
    const all = listEl.querySelectorAll(
      '[role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"]'
    );
    all.forEach((el) => {
      const closestMenu = el.closest('[role="menu"]');
      if (closestMenu === listEl) {
        el.setAttribute('tabindex', el === target ? '0' : '-1');
      }
    });
    target.focus();
  }

  /**
   * Keyboard handler for items within an open menu.
   * @param {KeyboardEvent} e
   * @param {string} menuId
   * @private
   */
  _handleMenuKeydown(e, menuId) {
    const modal = this._modals.get(menuId);
    if (!modal) return;
    const listEl = modal.querySelector(`#${menuId}MenuItems`);
    if (!listEl) return;

    const items = this._getNavigableItems(listEl);
    if (items.length === 0) return;

    const current = document.activeElement;
    const idx = items.indexOf(current);

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        e.stopPropagation();
        const next = idx < 0 ? 0 : (idx + 1) % items.length;
        this._focusMenuItem(listEl, items[next]);
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        e.stopPropagation();
        const prev = idx <= 0 ? items.length - 1 : idx - 1;
        this._focusMenuItem(listEl, items[prev]);
        break;
      }
      case 'Home': {
        e.preventDefault();
        e.stopPropagation();
        this._focusMenuItem(listEl, items[0]);
        break;
      }
      case 'End': {
        e.preventDefault();
        e.stopPropagation();
        this._focusMenuItem(listEl, items[items.length - 1]);
        break;
      }
      case 'Escape': {
        e.preventDefault();
        e.stopPropagation();
        this.closeAllMenus();
        // Restore focus to the menubar trigger
        const btn = this._buttons.get(menuId);
        if (btn) btn.focus();
        break;
      }
      case 'ArrowRight': {
        // Move to the next menubar item and open its menu
        e.preventDefault();
        e.stopPropagation();
        this._moveToAdjacentMenu(1);
        break;
      }
      case 'ArrowLeft': {
        // Move to the previous menubar item and open its menu
        e.preventDefault();
        e.stopPropagation();
        this._moveToAdjacentMenu(-1);
        break;
      }
      case 'Enter':
      case ' ': {
        e.preventDefault();
        e.stopPropagation();
        if (current && idx >= 0) {
          current.click();
        }
        break;
      }
      case 'Tab': {
        // Tab should close the menu and let focus leave the menubar naturally
        this.closeAllMenus();
        break;
      }
      default:
        // Character search: jump to first item starting with typed character
        if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
          e.stopPropagation();
          const char = e.key.toLowerCase();
          const startIdx = idx < 0 ? 0 : idx + 1;
          for (let offset = 0; offset < items.length; offset++) {
            const candidate = items[(startIdx + offset) % items.length];
            const label = candidate.textContent?.trim().toLowerCase();
            if (label && label.startsWith(char)) {
              this._focusMenuItem(listEl, candidate);
              break;
            }
          }
        }
        break;
    }
  }

  /**
   * From within an open menu, move to the next or previous menubar item and
   * open that menu (Arrow Left/Right from inside a menu).
   * @param {number} direction - +1 for next, -1 for previous
   * @private
   */
  _moveToAdjacentMenu(direction) {
    const barItems = this._getVisibleBarItems();
    if (barItems.length === 0) return;

    const idx = this._activeBarIndex;
    const next = (idx + direction + barItems.length) % barItems.length;
    this._focusBarItem(barItems, next);

    const nextId = this._menuIdForButton(barItems[next]);
    if (nextId) this.openMenu(nextId);
  }

  // ============================================================================
  // Menu item builders — APG roles
  // ============================================================================

  /**
   * Build a single menu item element from a declarative definition.
   *
   * Supported types:
   *   separator — <li role="separator">
   *   action    — <li role="none"><button role="menuitem">
   *   toggle    — <li role="none"><button role="menuitemcheckbox">
   *   submenu   — <li role="none"><button role="menuitem" aria-haspopup="menu">
   *               + nested <ul role="menu">
   *
   * @param {Object} item
   * @returns {HTMLLIElement}
   * @private
   */
  _buildMenuItem(item) {
    if (item.type === 'separator') {
      const sep = document.createElement('li');
      sep.setAttribute('role', 'separator');
      sep.className = 'menu-separator';
      return sep;
    }

    if (item.type === 'submenu') {
      return this._buildSubmenuItem(item);
    }

    const li = document.createElement('li');
    li.setAttribute('role', 'none');
    li.className = 'menu-item';

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'menu-item-btn';
    btn.setAttribute('tabindex', '-1');

    if (item.type === 'toggle') {
      btn.setAttribute('role', 'menuitemcheckbox');
      btn.setAttribute('aria-checked', String(Boolean(item.checked)));
    } else {
      btn.setAttribute('role', 'menuitem');
    }

    if (item.id) btn.id = item.id;

    const isDisabled = item.enabled === false || item.disabled === true;
    if (isDisabled) {
      btn.setAttribute('aria-disabled', 'true');
    }
    if (item.tooltip) {
      btn.setAttribute('title', item.tooltip);
    }

    const labelSpan = this._buildLabelSpan(item.label);
    btn.appendChild(labelSpan);

    const describedByIds = [];
    let tooltipSpan = null;

    if (item.tooltip) {
      const tooltipId = `menu-tip-${this._nextId()}`;
      tooltipSpan = document.createElement('span');
      tooltipSpan.id = tooltipId;
      tooltipSpan.className = 'sr-only';
      tooltipSpan.textContent = item.tooltip;
      describedByIds.push(tooltipId);
    }

    if (item.shortcutAction) {
      const shortcutDef = keyboardConfig.getShortcut(item.shortcutAction);
      if (shortcutDef) {
        const shortcutId = `menu-kbd-${this._nextId()}`;
        const kbdSpan = document.createElement('span');
        kbdSpan.id = shortcutId;
        kbdSpan.className = 'menu-item-shortcut';
        kbdSpan.setAttribute('aria-hidden', 'true');
        kbdSpan.textContent = formatShortcut(shortcutDef);
        btn.appendChild(kbdSpan);
        describedByIds.push(shortcutId);
      }
    }

    if (describedByIds.length > 0) {
      btn.setAttribute('aria-describedby', describedByIds.join(' '));
    }

    if (!isDisabled && typeof item.handler === 'function') {
      btn.addEventListener('click', () => {
        this.closeAllMenus();
        if (item.restoreFocus) this.restoreExternalFocus();
        item.handler();
      });
    }

    li.appendChild(btn);
    // The reason lives outside the button: inside it, it would join the
    // item's accessible name as well as being its description, so a screen
    // reader would read every disabled item's reason twice.
    if (tooltipSpan) li.appendChild(tooltipSpan);
    return li;
  }

  /**
   * A menu item's label span, with its access key underlined where upstream
   * marks one. Splitting the text into child spans does not change the text
   * content, so the accessible name is exactly the label either way.
   * @param {string} [label]
   * @returns {HTMLElement}
   * @private
   */
  _buildLabelSpan(label) {
    const span = document.createElement('span');
    span.className = 'menu-item-label';

    const text = label || '';
    const index = MNEMONICS.get(text);
    if (index === undefined || index >= text.length) {
      span.textContent = text;
      return span;
    }

    const key = document.createElement('span');
    key.className = 'menu-mnemonic';
    key.textContent = text[index];
    span.append(text.slice(0, index), key, text.slice(index + 1));
    return span;
  }

  /** @private */
  _nextId() {
    return ++this._idCounter;
  }

  /**
   * Build consecutive radio items as menuitemradio elements inside a
   * role="group" container with an accessible label.
   *
   * Returns an array of <li> elements (the group wrapper + each radio item)
   * to be appended to the parent menu list.
   *
   * @param {string} groupName
   * @param {Object[]} items
   * @returns {HTMLElement[]}
   * @private
   */
  _buildRadioGroup(groupName, items) {
    const groupLi = document.createElement('li');
    groupLi.setAttribute('role', 'none');
    groupLi.className = 'menu-item menu-item--radio-group';

    const groupDiv = document.createElement('div');
    groupDiv.setAttribute('role', 'group');
    groupDiv.setAttribute(
      'aria-label',
      RADIO_GROUP_LABELS[groupName] || groupName
    );
    groupDiv.className = 'menu-radio-group';

    const legend = document.createElement('span');
    legend.className = 'menu-radio-legend';
    legend.setAttribute('aria-hidden', 'true');
    legend.textContent = RADIO_GROUP_LABELS[groupName] || groupName;
    groupDiv.appendChild(legend);

    for (const item of items) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'menu-item-btn menu-radio-btn';
      btn.setAttribute('role', 'menuitemradio');
      btn.setAttribute('aria-checked', String(Boolean(item.checked)));
      btn.setAttribute('tabindex', '-1');

      const isDisabled = item.enabled === false || item.disabled === true;
      if (isDisabled) {
        btn.setAttribute('aria-disabled', 'true');
      }
      if (item.tooltip) {
        btn.setAttribute('title', item.tooltip);
      }

      btn.appendChild(this._buildLabelSpan(item.label));

      if (item.shortcutAction) {
        const shortcutDef = keyboardConfig.getShortcut(item.shortcutAction);
        if (shortcutDef) {
          const kbdSpan = document.createElement('span');
          kbdSpan.className = 'menu-item-shortcut';
          kbdSpan.setAttribute('aria-hidden', 'true');
          kbdSpan.textContent = formatShortcut(shortcutDef);
          btn.appendChild(kbdSpan);
        }
      }

      if (!isDisabled && typeof item.onChange === 'function') {
        btn.addEventListener('click', () => {
          this.closeAllMenus();
          item.onChange(item.value || item.label);
        });
      }

      groupDiv.appendChild(btn);
    }

    groupLi.appendChild(groupDiv);
    return [groupLi];
  }

  /**
   * Build a submenu item: a menuitem trigger with aria-haspopup="menu" and a
   * nested <ul role="menu">. The submenu opens on Enter/Space/ArrowRight and
   * closes on Escape/ArrowLeft (handled by the menu keydown handler).
   *
   * @param {Object} item
   * @returns {HTMLLIElement}
   * @private
   */
  _buildSubmenuItem(item) {
    const li = document.createElement('li');
    li.setAttribute('role', 'none');
    li.className = 'menu-item menu-item--submenu';

    const isDisabled = item.enabled === false || item.disabled === true;

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'menu-item-btn menu-submenu-trigger';
    btn.setAttribute('role', 'menuitem');
    btn.setAttribute('tabindex', '-1');
    btn.setAttribute('aria-haspopup', 'menu');
    btn.setAttribute('aria-expanded', 'false');

    if (isDisabled) {
      btn.setAttribute('aria-disabled', 'true');
    }
    if (item.tooltip) {
      btn.setAttribute('title', item.tooltip);
    }

    btn.appendChild(this._buildLabelSpan(item.label));

    // A submenu trigger can have a shortcut too (Window ▸ Jump To… is
    // Ctrl+J), so its key is shown here like any other item's.
    if (item.shortcutAction) {
      const shortcutDef = keyboardConfig.getShortcut(item.shortcutAction);
      if (shortcutDef) {
        const kbdSpan = document.createElement('span');
        kbdSpan.className = 'menu-item-shortcut';
        kbdSpan.setAttribute('aria-hidden', 'true');
        kbdSpan.textContent = formatShortcut(shortcutDef);
        btn.appendChild(kbdSpan);
      }
    }

    const arrow = document.createElement('span');
    arrow.className = 'menu-submenu-arrow';
    arrow.setAttribute('aria-hidden', 'true');
    arrow.textContent = '\u25B6';
    btn.appendChild(arrow);

    li.appendChild(btn);

    if (!isDisabled) {
      const submenuId = `submenu-${this._nextId()}`;
      const nestedUl = document.createElement('ul');
      nestedUl.id = submenuId;
      nestedUl.className = 'menu-items-list menu-items-list--nested hidden';
      nestedUl.setAttribute('role', 'menu');
      nestedUl.setAttribute('aria-label', item.label || '');

      btn.setAttribute('aria-controls', submenuId);

      // Radio runs collapse through _buildRadioGroup exactly as the
      // top-level renderer does. _buildMenuItem has no radio branch: children
      // of type 'radio' would fall through to plain menuitems with no
      // aria-checked and no click listener (radios carry onChange, not
      // handler), so every radio submenu would render inert.
      const childItems = Array.isArray(item.items) ? item.items : [];
      let ci = 0;
      while (ci < childItems.length) {
        const child = childItems[ci];
        if (child.type === 'radio') {
          const group = child.group;
          const radioItems = [];
          while (
            ci < childItems.length &&
            childItems[ci].type === 'radio' &&
            childItems[ci].group === group
          ) {
            radioItems.push(childItems[ci]);
            ci++;
          }
          this._buildRadioGroup(group, radioItems).forEach((el) =>
            nestedUl.appendChild(el)
          );
        } else {
          nestedUl.appendChild(this._buildMenuItem(child));
          ci++;
        }
      }

      // Submenu open/close via click on the trigger
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = btn.getAttribute('aria-expanded') === 'true';
        if (isOpen) {
          this._closeSubmenu(btn, nestedUl);
        } else {
          this._openSubmenu(btn, nestedUl);
        }
      });

      // Keyboard within submenu
      nestedUl.addEventListener('keydown', (e) => {
        this._handleSubmenuKeydown(e, btn, nestedUl);
      });

      li.appendChild(nestedUl);
    }

    return li;
  }

  // ============================================================================
  // Submenu open/close/keyboard
  // ============================================================================

  /** @private */
  _openSubmenu(triggerBtn, submenuUl) {
    triggerBtn.setAttribute('aria-expanded', 'true');
    submenuUl.classList.remove('hidden');
    const firstItem = this._getSubmenuNavigableItems(submenuUl)[0];
    if (firstItem) {
      firstItem.setAttribute('tabindex', '0');
      firstItem.focus();
    }
    // The list opens inline inside the menu's scrolling body. Focus brings
    // only its first item into view; the rest would sit below the fold, over
    // the overlay, where a click closes the menu instead.
    triggerBtn.closest('li')?.scrollIntoView?.({ block: 'nearest' });
  }

  /** @private */
  _closeSubmenu(triggerBtn, submenuUl) {
    triggerBtn.setAttribute('aria-expanded', 'false');
    submenuUl.classList.add('hidden');
    triggerBtn.focus();
  }

  /**
   * Get navigable items within a submenu (items whose closest role="menu" is this submenu).
   * @param {HTMLElement} submenuUl
   * @returns {HTMLElement[]}
   * @private
   */
  _getSubmenuNavigableItems(submenuUl) {
    const all = submenuUl.querySelectorAll(
      '[role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"]'
    );
    // Grayed-out items stay reachable here too (see _getNavigableItems).
    return Array.from(all).filter(
      (el) => el.closest('[role="menu"]') === submenuUl
    );
  }

  /**
   * Keyboard handler within a submenu.
   * @param {KeyboardEvent} e
   * @param {HTMLElement} triggerBtn - The button that opened this submenu
   * @param {HTMLElement} submenuUl - The <ul role="menu"> submenu
   * @private
   */
  _handleSubmenuKeydown(e, triggerBtn, submenuUl) {
    const items = this._getSubmenuNavigableItems(submenuUl);
    if (items.length === 0) return;

    const current = document.activeElement;
    const idx = items.indexOf(current);

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        e.stopPropagation();
        const next = idx < 0 ? 0 : (idx + 1) % items.length;
        this._focusSubmenuItem(submenuUl, items, items[next]);
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        e.stopPropagation();
        const prev = idx <= 0 ? items.length - 1 : idx - 1;
        this._focusSubmenuItem(submenuUl, items, items[prev]);
        break;
      }
      case 'ArrowLeft':
      case 'Escape': {
        e.preventDefault();
        e.stopPropagation();
        this._closeSubmenu(triggerBtn, submenuUl);
        break;
      }
      case 'Enter':
      case ' ': {
        e.preventDefault();
        e.stopPropagation();
        if (current && idx >= 0) current.click();
        break;
      }
      case 'Home': {
        e.preventDefault();
        e.stopPropagation();
        this._focusSubmenuItem(submenuUl, items, items[0]);
        break;
      }
      case 'End': {
        e.preventDefault();
        e.stopPropagation();
        this._focusSubmenuItem(submenuUl, items, items[items.length - 1]);
        break;
      }
      default:
        break;
    }
  }

  /** @private */
  _focusSubmenuItem(submenuUl, allItems, target) {
    for (const it of allItems) {
      it.setAttribute('tabindex', it === target ? '0' : '-1');
    }
    target.focus();
  }
}

// Singleton
let _instance = null;

/**
 * Get (or create) the ToolbarMenuController singleton.
 * @returns {ToolbarMenuController}
 */
export function getToolbarMenuController() {
  if (!_instance) {
    _instance = new ToolbarMenuController();
  }
  return _instance;
}

/**
 * Reset the singleton. Used in unit tests.
 */
export function resetToolbarMenuController() {
  _instance = null;
}

/**
 * Apply toolbar bar / workflow progress mutual exclusion based on UI mode.
 * @param {'simplified'|'standard'|'classic'} mode
 */
export function applyToolbarModeVisibility(mode) {
  const controller = getToolbarMenuController();

  const mainInterfaceEl = document.getElementById('mainInterface');
  const mainInterfaceVisible =
    mainInterfaceEl && !mainInterfaceEl.classList.contains('hidden');

  if (!mainInterfaceVisible) {
    controller.hide();
    hideWorkflowProgress();
    return;
  }

  showWorkflowProgress();

  if (mode !== 'simplified') {
    controller.show();
  } else {
    const uiMode = getUIModeController();
    const registry = uiMode.getRegistry();
    const menuIdMap = {
      toolbarMenuFile: 'file',
      toolbarMenuEdit: 'edit',
      toolbarMenuDesign: 'design',
      toolbarMenuView: 'view',
      toolbarMenuWindow: 'window',
      toolbarMenuHelp: 'help',
    };

    const visibleMenuIds = registry
      .filter((p) => p.id in menuIdMap)
      .filter((p) => {
        const el = document.getElementById(`${menuIdMap[p.id]}MenuBtn`);
        return el && !el.classList.contains('ui-mode-hidden');
      })
      .map((p) => menuIdMap[p.id]);

    if (visibleMenuIds.length > 0) {
      controller.setVisibleMenus(visibleMenuIds);
    } else {
      controller.hide();
    }
  }
}
